import { apiClient } from '../httpClient';
import { handleApiError } from '../utils/errorHandler';
import {
  subOrderListByIdsCache,
  subOrderListByIdsInflight,
  normalizeSubOrderIdForCache,
  inflightKeyForIds,
  invalidateSubOrderListByIdsCache
} from './subOrderListByIdsCache';
import {
  subOrderListByIdsWithoutNameCache,
  subOrderListByIdsWithoutNameInflight,
  invalidateSubOrderListByIdsWithoutNameCache
} from './subOrderListByIdsWithoutNameCache';

export const orderService = {
  // 주문 목록 조회
  getOrderList: async (params = {}) => {
    try {
      const response = await apiClient.get('/order/v3/main_order/list', { params });
      return { success: true, data: response.data };
    } catch (error) {
      return handleApiError(error, '주문 목록을 가져오는데 실패했습니다.');
    }
  },

  // 주문 생성
  createOrder: async (requestData) => {
    try {
      const response = await apiClient.post('/order/v3/main_order/item', requestData, {
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        }
      });
      console.log("[API] createOrder response", requestData, response);
      return { success: true, data: response.data };
    } catch (error) {
      console.error('createOrder 에러:', error);
      return handleApiError(error, '주문 생성에 실패했습니다.');
    }
  },

  // 주문 수정
  updateOrder: async (orderId, orderData) => {
    try {
      console.log("[API] updateOrder orderData", orderData);
      const response = await apiClient.put(`/order/v3/main_order/item/${orderId}`, orderData);
      console.log("[API] updateOrder response", response);
      return { success: true, data: response.data };
    } catch (error) {
      return handleApiError(error, '주문 수정에 실패했습니다.');
    }
  },

  // 주문 삭제
  deleteOrder: async (orderId) => {
    try {
      const response = await apiClient.delete(`/order/v3/main_order/item/${orderId}`);
      return { success: true, data: response.data };
    } catch (error) {
      return handleApiError(error, '주문 삭제에 실패했습니다.');
    }
  },

  // 주문 상세 조회
  getOrderDetail: async (orderId) => {
    try {
      const response = await apiClient.get(`/order/v3/main_order/item/${orderId}`);
      return { success: true, data: response.data };
    } catch (error) {
      return handleApiError(error, '주문 상세 정보를 가져오는데 실패했습니다.');
    }
  },

  /**
   * sub_order_id 목록으로 메인오더(서브오더 포함) 일괄 조회
   * POST /order/v3/main_order/list/by-sub-order-ids
   */
  getMainOrdersBySubOrderIds: async (subOrderIds = []) => {
    const unique = [];
    const seen = new Set();
    for (const raw of subOrderIds || []) {
      const n = Number(raw);
      if (!Number.isFinite(n) || n <= 0 || seen.has(n)) continue;
      seen.add(n);
      unique.push(n);
    }
    if (unique.length === 0) {
      return { success: true, data: [] };
    }
    try {
      const response = await apiClient.post('/order/v3/main_order/list/by-sub-order-ids', {
        sub_order_ids: unique,
      });
      return { success: true, data: response.data || [] };
    } catch (error) {
      return handleApiError(error, '서브오더 주문 정보를 가져오는데 실패했습니다.');
    }
  },

  getSubOrderDetail: async (subOrderId) => {
    try {
      const response = await apiClient.get(`/order/v3/sub_order/item/${subOrderId}`);
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  // dyeing_no_short로 서브오더 상세 조회
  getSubOrderDetailByDyeingNo: async (dyeingNoShort) => {
    try {
      const response = await apiClient.get('/production/work_order/v1/dyeing_request/detail', {
        params: { dyeing_no_short: dyeingNoShort }
      });
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  /**
   * 서브오더 공정 이벤트 + 재고 이동.
   * sub_order_id 와 dyeing_no_short 는 하나만 보낸다.
   */
  getProcessActivity: async ({ subOrderId, dyeingNoShort, limit = 50, offset = 0 } = {}) => {
    const params = { limit, offset };
    if (dyeingNoShort != null && String(dyeingNoShort).trim() !== '') {
      params.dyeing_no_short = dyeingNoShort;
    } else if (subOrderId != null && subOrderId !== '') {
      params.sub_order_id = subOrderId;
    }
    try {
      const response = await apiClient.get('/java/order/v1/sub_order/process_activity', { params });
      return { success: true, data: response.data };
    } catch (error) {
      return handleApiError(error, '공정 이벤트와 재고 이동을 가져오는데 실패했습니다.');
    }
  },

  /**
   * sub_order_id 목록으로 서브오더 정보 일괄 조회 (메모리 캐시 + 동일 배치 동시 요청 병합)
   */
  getSubOrderListByIds: async (subOrderIds = []) => {
    if (!Array.isArray(subOrderIds) || subOrderIds.length === 0) {
      return [];
    }

    const unique = [];
    const seen = new Set();
    for (const raw of subOrderIds) {
      const id = normalizeSubOrderIdForCache(raw);
      if (!id || seen.has(id)) continue;
      seen.add(id);
      unique.push(id);
    }
    if (unique.length === 0) {
      return [];
    }

    const missing = unique.filter((id) => !subOrderListByIdsCache.has(id));
    if (missing.length > 0) {
      const sorted = [...missing].sort();
      const inflightKey = inflightKeyForIds(sorted);

      if (!subOrderListByIdsInflight.has(inflightKey)) {
        const p = (async () => {
          try {
            const response = await apiClient.post('/order/v3/sub_order/list/by-ids', {
              sub_order_ids: missing,
            });
            const rows = response.data || [];
            const got = new Set();
            for (const row of rows) {
              if (row?.sub_order_id != null) {
                const k = String(row.sub_order_id);
                subOrderListByIdsCache.set(k, row);
                got.add(k);
              }
            }
            for (const id of missing) {
              if (!got.has(id)) {
                subOrderListByIdsCache.set(id, null);
              }
            }
          } finally {
            subOrderListByIdsInflight.delete(inflightKey);
          }
        })();
        subOrderListByIdsInflight.set(inflightKey, p);
      }

      await subOrderListByIdsInflight.get(inflightKey);
    }

    return unique.map((id) => subOrderListByIdsCache.get(id)).filter((row) => row != null);
  },

  /**
   * sub_order_id 목록 조회 — 이름 필드 없음 (가벼운 조인). 출고 일괄 등록 DETAIL 등용.
   * POST /order/v3/sub_order/list/by-ids-without-name
   */
  getSubOrderListByIdsWithoutName: async (subOrderIds = []) => {
    if (!Array.isArray(subOrderIds) || subOrderIds.length === 0) {
      return [];
    }

    const unique = [];
    const seen = new Set();
    for (const raw of subOrderIds) {
      const id = normalizeSubOrderIdForCache(raw);
      if (!id || seen.has(id)) continue;
      seen.add(id);
      unique.push(id);
    }
    if (unique.length === 0) {
      return [];
    }

    const missing = unique.filter((id) => !subOrderListByIdsWithoutNameCache.has(id));
    if (missing.length > 0) {
      const sorted = [...missing].sort();
      const inflightKey = inflightKeyForIds(sorted);

      if (!subOrderListByIdsWithoutNameInflight.has(inflightKey)) {
        const p = (async () => {
          try {
            const response = await apiClient.post('/order/v3/sub_order/list/by-ids-without-name', {
              sub_order_ids: missing.map((id) => Number(id)).filter((n) => !Number.isNaN(n))
            });
            const rows = response.data || [];
            const got = new Set();
            for (const row of rows) {
              if (row?.sub_order_id != null) {
                const k = String(row.sub_order_id);
                subOrderListByIdsWithoutNameCache.set(k, row);
                got.add(k);
              }
            }
            for (const id of missing) {
              if (!got.has(id)) {
                subOrderListByIdsWithoutNameCache.set(id, null);
              }
            }
          } finally {
            subOrderListByIdsWithoutNameInflight.delete(inflightKey);
          }
        })();
        subOrderListByIdsWithoutNameInflight.set(inflightKey, p);
      }

      await subOrderListByIdsWithoutNameInflight.get(inflightKey);
    }

    return unique.map((id) => subOrderListByIdsWithoutNameCache.get(id)).filter((row) => row != null);
  },

  /** 로그아웃 등에서 서브오더 by-ids 캐시 비우기 (구현은 subOrderListByIdsCache.js) */
  invalidateSubOrderListByIdsCache,

  invalidateSubOrderListByIdsWithoutNameCache,

  /**
   * SubOrderV3 배치 조회 (item_name·uom_name·color 등, 메인오더 확장 없음)
   * POST /order/v3/sub_order/sub_order_list_by_ids
   */
  getSubOrderListSubOrderV3ByIds: async (subOrderIds = []) => {
    if (!Array.isArray(subOrderIds) || subOrderIds.length === 0) {
      return [];
    }
    try {
      const response = await apiClient.post('/order/v3/sub_order/sub_order_list_by_ids', {
        sub_order_ids: subOrderIds,
      });
      return response.data || [];
    } catch (error) {
      throw error;
    }
  },

  /**
   * Internal Production Order 생성
   * POST /java/order/v1/internal_production
   */
  createInternalProductionOrder: async (requestData) => {
    try {
      const response = await apiClient.post('/java/order/v1/internal_production', requestData, {
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        }
      });
      return { success: true, data: response.data };
    } catch (error) {
      return handleApiError(error, '내부 생산 오더 생성에 실패했습니다.');
    }
  },

  /**
   * Internal Production Order 목록
   * GET /java/order/v1/internal_production
   */
  getInternalProductionList: async (params = {}) => {
    try {
      const response = await apiClient.get('/java/order/v1/internal_production', { params });
      return { success: true, data: response.data };
    } catch (error) {
      return handleApiError(error, '내부 생산 오더 목록을 가져오는데 실패했습니다.');
    }
  },

  /**
   * Main order 상세 조회 (SALES / INTERNAL_PRODUCTION 공통)
   * GET /java/order/v3/main_order/item/{orderId}
   */
  getMainOrderJava: async (orderId) => {
    try {
      const response = await apiClient.get(`/java/order/v3/main_order/item/${orderId}`);
      return { success: true, data: response.data };
    } catch (error) {
      return handleApiError(error, '주문 상세 정보를 가져오는데 실패했습니다.');
    }
  },

  /**
   * Sales Main order 생성
   * POST /java/order/v3/main_order/item
   */
  createMainOrderJava: async (requestData) => {
    try {
      const response = await apiClient.post('/java/order/v3/main_order/item', requestData, {
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        }
      });
      return { success: true, data: response.data };
    } catch (error) {
      return handleApiError(error, '주문 생성에 실패했습니다.');
    }
  },

  /**
   * Main order 수정 (SALES / INTERNAL_PRODUCTION 공통)
   * PUT /java/order/v3/main_order/item/{orderId}
   */
  updateMainOrderJava: async (orderId, requestData) => {
    try {
      const response = await apiClient.put(`/java/order/v3/main_order/item/${orderId}`, requestData, {
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        }
      });
      return { success: true, data: response.data };
    } catch (error) {
      return handleApiError(error, '오더 수정에 실패했습니다.');
    }
  },

  /**
   * Main order 삭제 (SALES / INTERNAL_PRODUCTION 공통)
   * DELETE /java/order/v3/main_order/item/{orderId}
   */
  deleteMainOrderJava: async (orderId) => {
    try {
      const response = await apiClient.delete(`/java/order/v3/main_order/item/${orderId}`);
      return { success: true, data: response.data };
    } catch (error) {
      return handleApiError(error, '오더 삭제에 실패했습니다.');
    }
  },

  // 오더 ID 또는 스타일 번호로 검색
  // 입력이 숫자만이면 order_id와 style_no 모두 전달, 문자가 포함되면 style_no만 전달
  searchOrders: async (query) => {
    try {
      const trimmed = typeof query === 'string' ? query.trim() : '';
      const isNumericOnly = /^\d+$/.test(trimmed);
      const params = {};
      if (isNumericOnly && trimmed) {
        params.order_id = parseInt(trimmed, 10);
        params.style_no = trimmed;
      } else if (trimmed) {
        params.style_no = trimmed;
      }
      const response = await apiClient.get('/order/v3/search/list', { params });
      return { success: true, data: response.data };
    } catch (error) {
      return handleApiError(error, '오더 검색에 실패했습니다.');
    }
  },
}; 